import React, { useState } from "react";
import { ShieldAlert, EyeOff, Eye, Trash2, Loader2, X } from "lucide-react";
import { createPortal } from "react-dom";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import UndoAction from "../UndoAction";
import { ArchiveReasonPicker, composeReason } from "./ArchiveReasons";

// Admin moderation without leaving the page you spotted the problem on. Renders nothing at all
// for anyone who isn't an admin. English only, like the rest of the admin surface.
export const AdminDealActions = ({ deal, variant = "card", onArchived, onDeleted, onRestored }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("");        // "archive" | "delete"
  const [reason, setReason] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggleReason = (r) => setPicked((p) => (p.includes(r) ? p.filter((x) => x !== r) : [...p, r]));

  if (user?.role !== "admin") return null;
  const hidden = !!deal.hidden;

  const archive = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/deals/${deal.id}/review`, { action: "hide", reason: composeReason(picked, reason) });
      toast({ title: "Deal archived", description: `${deal.title} — the shop has been told why.` });
      setMode(""); setReason(""); setPicked([]); setOpen(false);
      onArchived?.();
    } catch (e) { toast({ title: "Could not archive", description: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/deals/${deal.id}/unhide`);
      toast({ title: "Deal restored" });
      setOpen(false);
      onRestored?.();
    } catch { toast({ title: "Could not restore" }); }
    finally { setBusy(false); }
  };

  const undelete = async () => {
    try { await api.post(`/admin/deals/${deal.id}/undelete`); toast({ title: "Deal brought back" }); onRestored?.(); }
    catch (e) { toast({ title: "Could not undo", description: e.response?.data?.detail || "It may already be purged." }); }
  };

  const destroy = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/deals/${deal.id}`);
      toast({ title: "Deal deleted", description: deal.title,
        action: <UndoAction onUndo={undelete} testid="undo-delete-deal" /> });
      setMode(""); setOpen(false);
      onDeleted?.();
    } catch (e) { toast({ title: "Delete failed", description: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const dialog = mode && createPortal(
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { stop(e); setMode(""); }} data-testid="admin-deal-dialog">
      <div onClick={stop} role="dialog" aria-modal="true"
        className="bg-card w-full max-w-md rounded-2xl border border-border shadow-2xl p-5 space-y-3">
        <h3 className="font-extrabold text-foreground">
          {mode === "delete" ? `Delete “${deal.title}” for good?` : `Archive “${deal.title}”?`}
        </h3>
        {mode === "delete" ? (
          <p className="text-sm text-muted-foreground">
            This wipes the deal off LytePress immediately — {deal.business} loses it, along with its page and any shared
            links. You'll get one chance to undo it straight after. Use Archive instead if you only want it out of sight.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Hides it from shoppers and emails {deal.business} the reason. Reversible, and it adds a strike to the shop.
            </p>
            <ArchiveReasonPicker picked={picked} onToggle={toggleReason} />
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="Anything to add? (optional)" data-testid="admin-archive-reason"
              className="w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#16a34a]/40" />
            {composeReason(picked, reason) && (
              <p className="text-xs text-muted-foreground" data-testid="admin-archive-preview">
                <span className="font-semibold text-foreground">They'll be told:</span> {composeReason(picked, reason)}
              </p>
            )}
          </>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={() => setMode("")} data-testid="admin-deal-cancel"
            className="text-sm font-semibold px-4 py-2 rounded-xl border border-border hover:bg-secondary transition-colors">Cancel</button>
          <button onClick={mode === "delete" ? destroy : archive} disabled={busy} data-testid="admin-deal-confirm"
            className={`text-sm font-bold px-4 py-2 rounded-xl text-white transition-colors disabled:opacity-60 ${mode === "delete" ? "bg-rose-600 hover:bg-rose-700" : "bg-rose-500 hover:bg-rose-600"}`}>
            {busy ? <Loader2 className="animate-spin" size={15} /> : mode === "delete" ? "Yes, delete it" : "Archive deal"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

  if (variant === "page") {
    return (
      <>
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-rose-300/60 bg-rose-50/60 dark:bg-rose-950/20 p-3" data-testid="admin-deal-actions">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-600 mr-1"><ShieldAlert size={14} /> Admin</span>
          {hidden ? (
            <button onClick={restore} disabled={busy} data-testid="admin-restore-deal"
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border bg-card hover:bg-secondary rounded-xl px-3 py-2 transition-colors">
              {busy ? <Loader2 className="animate-spin" size={13} /> : <Eye size={13} />} Restore
            </button>
          ) : (
            <button onClick={() => setMode("archive")} data-testid="admin-archive-deal"
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border bg-card hover:bg-secondary rounded-xl px-3 py-2 transition-colors">
              <EyeOff size={13} /> Archive
            </button>
          )}
          <button onClick={() => setMode("delete")} data-testid="admin-delete-deal"
            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-rose-300 text-rose-600 bg-card hover:bg-rose-100 dark:hover:bg-rose-950/40 rounded-xl px-3 py-2 transition-colors">
            <Trash2 size={13} /> Delete
          </button>
          {hidden && <span className="text-xs font-semibold text-rose-600">Archived — shoppers can't see this.</span>}
        </div>
        {dialog}
      </>
    );
  }

  return (
    <>
      <button onClick={(e) => { stop(e); setOpen((o) => !o); }} data-testid={`admin-card-menu-${deal.id}`}
        title="Admin actions" aria-label="Admin actions"
        className="h-8 w-8 rounded-full bg-rose-600/90 text-white flex items-center justify-center hover:scale-110 hover:bg-rose-600 transition-all">
        <ShieldAlert size={15} />
      </button>
      {open && (
        <div onClick={stop} data-testid={`admin-card-actions-${deal.id}`}
          className="absolute top-12 right-3 z-20 w-44 bg-card border border-border rounded-xl shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Admin</span>
            <button onClick={(e) => { stop(e); setOpen(false); }} aria-label="Close" className="text-muted-foreground hover:text-foreground"><X size={13} /></button>
          </div>
          {hidden ? (
            <button onClick={restore} disabled={busy} data-testid={`admin-card-restore-${deal.id}`}
              className="w-full text-left px-3 py-2.5 text-xs font-semibold hover:bg-secondary flex items-center gap-2 transition-colors">
              {busy ? <Loader2 className="animate-spin" size={13} /> : <Eye size={13} />} Restore
            </button>
          ) : (
            <button onClick={(e) => { stop(e); setMode("archive"); }} data-testid={`admin-card-archive-${deal.id}`}
              className="w-full text-left px-3 py-2.5 text-xs font-semibold hover:bg-secondary flex items-center gap-2 transition-colors">
              <EyeOff size={13} /> Archive
            </button>
          )}
          <button onClick={(e) => { stop(e); setMode("delete"); }} data-testid={`admin-card-delete-${deal.id}`}
            className="w-full text-left px-3 py-2.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center gap-2 transition-colors">
            <Trash2 size={13} /> Delete
          </button>
        </div>
      )}
      {dialog}
    </>
  );
};

export default AdminDealActions;
